import type {Document, EmptyObject, ExactUserRawBodyMailSendRequest, Loan, ProvidedSeeking, SeekedSeeking, Seeking, SmsSendRequest, User} from 'src/typings';
import type {ModalDialogService} from 'shared/modal-dialogs';
import type {MailerService} from 'shared/mailer';
import type {FinishedResponseInteractionService, ReloaderService, SmsService, WalkerService} from 'shared/services';
import type {LoanList, SeekingList, UserAccountPageLoadedData} from './types';
import type {DelayedReturnPenalty, UserDetailView, UserId} from '../types';
import type {PageableListState} from 'shared/utils/reactive-pageable-list';
import type UserService from '../user.service';
import type {CancellationDataService} from '../../loan/cancellation.data-service';
import type {KpUserFinancePanelService} from 'src/features/user/kp-user-finance-panel/kp-user-finance-panel.service';
import type {UserPreferencesService} from '../user-preferences/user-preferences.service';
import type {LogService} from 'core/logging/log.service';
import type ReaderLoanDataService from '../reader-loan.data-service';
import type LoanDataService from '../../loan/loan.data-service';
import type LoanService from '../../loan/loan.service';
import type SeekingDataService from '../../ill/seeking.data-service';
import type UserDataService from '../user.data-service';
import type RecordDataService from '../../record/record.data-service';
import {transferify} from 'shared/utils/data-service-utils';
import {DefaultReactivePageableList} from 'shared/utils/default-reactive-pageable-list';
import {exists} from 'shared/utils/custom-utils';
import {USER_ACCOUNT_RELOAD_RETURN_PENALTY_EVENT} from './types';

export class KpUserAccountPageService {
    public static serviceName = 'kpUserAccountService';
    private eventTarget = new EventTarget();

    /*@ngInject*/
    constructor(private readerLoanDataService: ReaderLoanDataService,
                private loanDataService: LoanDataService,
                private userService: UserService,
                private reloader: ReloaderService,
                private loanService: LoanService,
                private mailerService: MailerService,
                private smsService: SmsService,
                private walker: WalkerService,
                private recordDataService: RecordDataService,
                private modalDialogService: ModalDialogService,
                private userDataService: UserDataService,
                private seekingDataService: SeekingDataService,
                private userPreferencesService: UserPreferencesService,
                private cancellationDataService: CancellationDataService,
                private finishedResponseInteractionService: FinishedResponseInteractionService,
                private kpUserFinancePanelService: KpUserFinancePanelService,
                private logService: LogService) {
    }

    public async loadUserAccountPageData(userId: UserId): Promise<UserAccountPageLoadedData | null> {
        let user: User;
        let detailView: UserDetailView;
        let userRecord: Document | null = null;

        try {
            user = await this.userService.getById(userId);
            detailView = await this.userDataService.getUserDetailView(userId);
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
            return null;
        }

        if (exists(user.rid)) {
            try {
                userRecord = await this.recordDataService.getById(user.rid) as Document;
            } catch (exceptionResponse) {
                this.logService.warn(`Can not load user record by id: ${user.rid}`, exceptionResponse);
            }
        }

        const loanList = new DefaultReactivePageableList<Loan, string>({
                loadListItems: (pageable) => this.readerLoanDataService.getAllLoans(user.id, pageable),
                loadListSize: () => this.readerLoanDataService.getAllLoansSize(user.id),
                loadSingleItemFunction: (id: string) => this.loanDataService.getById(id)
            }
        );

        const waitingLoanList = new DefaultReactivePageableList<Loan, string>({
            loadListItems: (pageable) => this.readerLoanDataService.getAllWaitingLoans(user.id, pageable),
            loadListSize: () => this.readerLoanDataService.getAllWaitingLoansSize(user.id),
            loadSingleItemFunction: (id) => this.loanDataService.getById(id)
        });

        const endedLoanList = new DefaultReactivePageableList<Loan, string>({
            loadListItems: (pageable) => this.readerLoanDataService.getAllEndedLoans(user.id, pageable),
            loadListSize: () => this.readerLoanDataService.getAllEndedLoansSize(user.id),
            loadSingleItemFunction: (id) => this.loanDataService.getById(id)
        });

        const seekingsOfRequester = new DefaultReactivePageableList<Seeking, string>({
                loadListItems: (pageable) => this.readerLoanDataService.getAllIllSeekingsOfRequester(user.id, pageable),
                loadListSize: () => this.readerLoanDataService.getAllIllSeekingsOfRequesterSize(user.id)
            }
        );

        const providedSeekingsOfSeeker = new DefaultReactivePageableList<ProvidedSeeking, string>({
            loadListItems: (pageable) => this.readerLoanDataService.getAllIllProvidedSeekingsOfSeeker(user.id, pageable),
            loadListSize: () => this.readerLoanDataService.getAllIllProvidedSeekingsOfSeekerSize(user.id)
        });

        const seekingsOfProvider = new DefaultReactivePageableList<SeekedSeeking, string>({
            loadListItems: (pageable) => this.readerLoanDataService.getAllIllSeekingsOfProvider(user.id, pageable),
            loadListSize: () => this.readerLoanDataService.getAllIllSeekingsOfProviderSize(user.id)
        });

        const delayedReturnPenalty = await this.userService.getDelayedReturnPenalty(user);

        return {
            loanList,
            waitingLoanList,
            endedLoanList,
            seekingsOfRequester,
            providedSeekingsOfSeeker,
            seekingsOfProvider,
            delayedReturnPenalty,
            user,
            detailView,
            userRecord
        };
    }

    public async reloadRoute(): Promise<void> {
        await this.reloader.reloadRoute();
    }

    public async editUser(user: User) {
        await this.userService.editUserByForm(user);
        await this.reloadRoute();
    }

    public async deleteUser(user: User) {
        await this.userService.deleteUser(user);
        await this.reloadRoute();
    }

    public async userPreferences(user: User) {
        await this.userPreferencesService.setUserPreferences(user);
    }

    public async showPasswordChangeForm(user: User) {
        return this.userService.showPasswordChangeForm(user);
    }

    public async deleteCredentials(user: User) {
        await this.userService.deleteCredentials(user);
        await this.reloadRoute();
    }

    public async deleteLinkWithMojeID(user: User) {
        await this.userService.deleteLinkWithMojeID(user);
        await this.reloadRoute();
    }

    public async deleteLinkWithBankID(user: User) {
        await this.userService.deleteLinkWithBankID(user);
        await this.reloadRoute();
    }

    public async extendRegistration(user: User) {
        await this.userService.extendRegistration(user);
        await this.reloadRoute();
    }

    public async cancelLoan(loan: Loan, waitingLoanList: LoanList) {
        try {
            const actionResponse = await this.cancellationDataService.cancelLoan(loan);
            await this.finishedResponseInteractionService.showSuccessResponseInToastIfPossible(actionResponse);
            await waitingLoanList.resetAndReloadFirstPage(); // TODO: maybe just remove canceled loan
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
        }
    }

    public async renewAllLoans(user: User, loanList: LoanList) {
        try {
            const actionResponse = await this.loanDataService.renewAllLoans(user);
            await this.finishedResponseInteractionService.showSuccessResponseInModalWindow(actionResponse);
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedInModalWindow(exceptionResponse);
        }
        await loanList.reloadAllLoaded();
    }

    public async renewLoan(loan: Loan, loanList: LoanList) {
        try {
            const actionResponse = await this.loanDataService.renewLoan(loan);
            await this.finishedResponseInteractionService.showSuccessResponseInToastIfPossible(actionResponse);
            await loanList.reloadItem(loan);
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
        }
    }

    public async showLendingForm(user: User, loanList: LoanList, waitingLoanList: LoanList) {
        const successResponses = await this.loanService.showLendingForm(user);

        if (successResponses.length > 0) {
            await Promise.all([
                loanList.resetAndReloadFirstPage(),
                waitingLoanList.resetAndReloadFirstPage(),
                this.loadDelayedReturnPenalty(user)
            ]);

            this.kpUserFinancePanelService.updateFinancePanel();
        }
    }

    public async showReturningForm(user: User, loanList: LoanList, endedLoanList: LoanList) {
        try {
            await this.loanService.showReturningForm();
        } finally {
            await Promise.all([
                loanList.resetAndReloadFirstPage(),
                endedLoanList.resetAndReloadFirstPage(),
                this.loadDelayedReturnPenalty(user)
            ]);

            this.kpUserFinancePanelService.updateFinancePanel();
        }
    }

    public async returnLoan(user: User, loan: Loan, loanList: LoanList, endedLoanList: LoanList): Promise<void> {
        try {
            await this.loanService.returnLoanAndShowResult(loan);
        } finally {
            await Promise.all([
                loanList.resetAndReloadFirstPage(),
                endedLoanList.resetAndReloadFirstPage(),
                this.loadDelayedReturnPenalty(user)
            ]);

            this.kpUserFinancePanelService.updateFinancePanel();
        }
    }

    public async createSeekingRequest(user: User, seekingsOfRequester: SeekingList) {
        try {
            const actionResponse = await this.seekingDataService.createSeekingRequest({requester: user});
            await this.finishedResponseInteractionService.showSuccessResponseInToastIfPossible(actionResponse);
            await seekingsOfRequester.resetAndReloadFirstPage();
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
        }
    }

    public async loadDelayedReturnPenalty(user: User): Promise<void> {
        const delayedReturnPenalty = await this.userService.getDelayedReturnPenalty(user);
        this.eventTarget.dispatchEvent(new CustomEvent<DelayedReturnPenalty>(USER_ACCOUNT_RELOAD_RETURN_PENALTY_EVENT, {detail: delayedReturnPenalty}));
    }

    public enableRenewAllButton(loanListState: PageableListState<Loan, string, EmptyObject>) {
        if (!loanListState.isPartiallyLoaded || loanListState.loadedSize === 0) {
            return false;
        }

        if (!loanListState.isAllLoaded) {
            return true;
        }

        return loanListState.loadedItems.some((loan) => loan.renewability.renewable);
    }

    public enableReturnButton(loanListState: PageableListState<Loan, string, EmptyObject>) {
        return loanListState.isPartiallyLoaded && loanListState.loadedSize > 0;
    }

    public async sendEmail(user: User): Promise<void> {
        const request = {recipients: [user], attachments: []} as ExactUserRawBodyMailSendRequest;
        return this.mailerService.sendMailToExactUsers(request);
    }

    public async sendSms(user: User): Promise<void> {
        const request = {recipients: [user]} as SmsSendRequest;
        return this.smsService.sendSms(request);
    }

    public async discountRequest(user: User): Promise<void> {
        await this.modalDialogService.openModalWindow('discountRequest', {user, showSuccessModal: true});
    }

    public async printReceiptOfPayment(user: User) {
        await this.walker.newPageInNewWindow('/users/print-receipt-of-payment', transferify({user}));
    }

    public async printRegistrationAgreement(user: User) {
        await this.walker.newPageInNewWindow(`/users/${user.id}/print-registration-agreement`);
    }

    public async printLoanConfirmation(user: User) {
        await this.walker.newPageInNewWindow(`/users/${user.id}/print-loan-confirmation`);
    }

    public addReloadDelayedReturnPenaltyHandler(callback: (event: CustomEvent<DelayedReturnPenalty>) => void) {
        this.eventTarget.addEventListener(USER_ACCOUNT_RELOAD_RETURN_PENALTY_EVENT, callback);
        return () => this.eventTarget.removeEventListener(USER_ACCOUNT_RELOAD_RETURN_PENALTY_EVENT, callback);
    }
}